import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Avatar, TextField, Typography } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { FlexBox, HandlerError, IconButton, LoadingButton } from '@dfl/mui-react-common';
import DeleteIcon from 'components/icons/DeleteIcon';
import { DASHBOARD_THEME_COLORS } from 'modules/dashboard/constants/dashboard-colors';
import { useState, useEffect } from 'react';

type DeleteActionProps = {
  disabled?: boolean;
  error?: any;
  onOpen: () => void;
  open: boolean;
  isLoading?: boolean;
  onClose: () => void;
  errors?: any;
  data: any;
  onConfirm: () => void;
  confirmationValue?: string;
};

const DeleteRowAction = ({
  disabled,
  onConfirm,
  onOpen,
  open,
  error,
  onClose,
  errors,
  isLoading,
  confirmationValue,
}: DeleteActionProps) => {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState('');
  const [showError, setShowError] = useState(false);

  const isConfirmDisabled = confirmationValue ? inputValue !== confirmationValue : false;

  useEffect(() => {
    if (!open) {
      setInputValue('');
      setShowError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmationValue && inputValue !== confirmationValue) {
      setShowError(true);
      return;
    }
    onConfirm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowError(false);
  };

  return (
    <>
      <IconButton onClick={onOpen} tooltip={t('delete')} disabled={disabled}>
        <DeleteIcon color='error' />
      </IconButton>

      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
        maxWidth={'xs'}
      >
        <DialogTitle sx={{ padding: '16px 16px 8px 16px' }} lineHeight={1.5} id='delete-dialog-title'>
          <FlexBox alignItems={'center'} gap={2}>
            <Avatar sx={{ backgroundColor: DASHBOARD_THEME_COLORS.DANGER_LIGHT_COLOR, width: 30, height: 30, borderRadius: 2 }} variant='rounded'>
              <DeleteIcon color='error' fontSize='small' />
            </Avatar>
            {t('deleteConfirmation.title')}
          </FlexBox>
        </DialogTitle>
        <DialogContent>
          <HandlerError error={error} errors={errors} />
          {confirmationValue && (
            <>
              <Typography variant='body2' sx={{ mt: 2, mb: 1 }}>
                <Trans
                  i18nKey='deleteConfirmation.typeToConfirm'
                  ns='common'
                  values={{ value: confirmationValue }}
                  components={{ bold: <strong /> }}
                />
              </Typography>
              <TextField
                fullWidth
                size='small'
                value={inputValue}
                onChange={handleInputChange}
                placeholder={t('deleteConfirmation.typePlaceholder')}
                error={showError}
                helperText={showError ? t('deleteConfirmation.valueDoesNotMatch') : ''}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='grey' onClick={onClose}>
            {t('cancel')}
          </Button>
          <LoadingButton
            disabled={disabled || isConfirmDisabled}
            onClick={handleConfirm}
            autoFocus={!confirmationValue}
            color='error'
            variant={'contained'}
            loading={isLoading}
          >
            {t('confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteRowAction;
